import type { UploadProgressHandler, UploadSession } from "common/components/types";
import { isUploadFileResponseError } from "common/components/types";
import { nullToUndefined } from "common/Support";
import type { WireFilePickerComponent, WireImagePickerComponent } from "common/wire/base-components";
import type { WireEditableValue } from "common/wire/types";
import { uploadAppFile } from "lib/upload-app-file";
import * as React from "react";
import type { MonotoneIcons } from "sharedUI/ui/views/glide-icon/glide-icon";
import type { WireBackend } from "webapp/hydrated-ui/types";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { showUploadFailedRetryModal } from "webapp/lib/in-app-modals";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";
import WireCoreFilePicker from "./wire-core-file-picker";

interface Props {
    readonly title?: string;
    readonly value: WireEditableValue<string>;
    readonly backend: WireBackend;
    readonly mimePattern?: string;
    readonly valueLabel?: string;
    readonly iconSource: MonotoneIcons;
    readonly isRequired: boolean;
    readonly showPreview: boolean;
    readonly isBusy: WireEditableValue<boolean>;
}

const Picker: React.VFC<Props> = p => {
    const { showPreview, valueLabel, value, title, backend, mimePattern, iconSource, isRequired, isBusy } = p;
    const { appKind } = backend;

    const valueTokenRef = React.useRef<string>();
    valueTokenRef.current = value.onChangeToken;

    const isBusyTokenRef = React.useRef<string>();
    isBusyTokenRef.current = isBusy.onChangeToken;

    // NOTE: We're assuming that whenever this is changed we're also doing
    // something else to trigger a repaint.
    const isCancelledRef = React.useRef(false);

    const [lastValue, setLastValue] = React.useState<string>();
    const [uploadSession, setUploadSession] = React.useState<UploadSession>();

    const setUploadSessionAndIsBusy = React.useCallback(
        (session: UploadSession | undefined) => {
            setUploadSession(session);
            if (isBusyTokenRef.current !== undefined) {
                backend.valueChanged(isBusyTokenRef.current, session !== undefined);
            }
        },
        [backend]
    );

    const onChange = React.useCallback(
        (newValue: string) => {
            if (valueTokenRef.current === undefined) return;
            backend.valueChanged(valueTokenRef.current, newValue);
        },
        [backend]
    );

    const onNewURL = React.useCallback(
        (newURL: string | undefined) => {
            if (newURL !== undefined) {
                onChange(newURL);
            }

            return newURL !== undefined;
        },
        [onChange]
    );

    const onDropFile = React.useCallback(
        (file: File, progressHandler: UploadProgressHandler) => {
            async function f() {
                setLastValue(value.value);
                isCancelledRef.current = false;
                if (uploadSession !== undefined) {
                    uploadSession.cancel();
                }
                const session = uploadAppFile(
                    backend.appID,
                    appKind,
                    getAppFacilities(),
                    file,
                    "image-picker",
                    progressHandler,
                    () => undefined
                );
                setUploadSessionAndIsBusy(session);

                const clearUploadSession = () => {
                    if (uploadSession === session) {
                        setUploadSessionAndIsBusy(undefined);
                    } else {
                        if (isBusyTokenRef.current !== undefined) {
                            backend.valueChanged(isBusyTokenRef.current, false);
                        }
                    }
                };

                while (true) {
                    const response = await session.attempt();
                    if (isCancelledRef.current) return false;
                    if (!isUploadFileResponseError(response)) {
                        clearUploadSession();
                        return onNewURL(response.path);
                    }
                    if (!(await showUploadFailedRetryModal(response, appKind))) {
                        clearUploadSession();
                        return false;
                    }
                }
            }

            return f();
        },
        [backend, onNewURL, uploadSession, value.value, appKind, setUploadSessionAndIsBusy]
    );

    const onCancel = React.useCallback(
        (cancelled: boolean) => {
            if (cancelled && !isCancelledRef.current) {
                isCancelledRef.current = true;
                if (uploadSession !== undefined) {
                    uploadSession.cancel();
                    setUploadSessionAndIsBusy(undefined);
                }
                onNewURL(lastValue);
            }
        },
        [lastValue, onNewURL, uploadSession, setUploadSessionAndIsBusy]
    );

    return (
        <WireCoreFilePicker
            value={value.value}
            valueLabel={valueLabel}
            isEnabled={value.onChangeToken !== undefined}
            mimePattern={mimePattern}
            iconSource={iconSource}
            showPreview={showPreview}
            title={title}
            appKind={appKind}
            onDropFile={onDropFile}
            onChange={onChange}
            onCancel={onCancel}
            isRequired={isRequired}
        />
    );
};

export const WireImagePicker: WireRenderer<WireImagePickerComponent> = React.memo(p => {
    return (
        <Picker
            title={nullToUndefined(p.title)}
            value={p.value}
            valueLabel={p.value.value || "Choose an image..."}
            backend={p.backend}
            showPreview={true}
            mimePattern={"image/*"}
            iconSource={"mt-page-image-upload"}
            isRequired={p.isRequired}
            isBusy={p.isBusy}
        />
    );
});

export const WireFilePicker: WireRenderer<WireFilePickerComponent> = React.memo(p => {
    return (
        <Picker
            title={nullToUndefined(p.title)}
            valueLabel={p.value.value || "Choose a file..."}
            showPreview={false}
            value={p.value}
            backend={p.backend}
            iconSource={"mt-page-file-upload"}
            isRequired={p.isRequired}
            isBusy={p.isBusy}
        />
    );
});
